import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const COMMITTEE_EMAIL = "info@rcnmo.com";

const schema = z.object({
  email: z.string().trim().email("Please enter a valid email").max(255),
  first_name: z.string().trim().min(1, "Required").max(80),
  last_name: z.string().trim().min(1, "Required").max(80),
  company: z.string().trim().min(1, "Required").max(120),
  job_title: z.string().trim().min(1, "Required").max(120),
  industry: z.string().trim().min(1, "Required").max(120),
  phone: z.string().trim().max(40).optional().or(z.literal("")),
  chapter: z.enum(["washington", "union"], { required_error: "Choose a chapter" }),
  heard_about: z.string().trim().min(1, "Required").max(500),
  consent: z.literal(true, {
    errorMap: () => ({ message: "You must agree to be contacted by email" }),
  }),
});

type FormState = {
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  job_title: string;
  industry: string;
  phone: string;
  chapter: "washington" | "union" | "";
  heard_about: string;
  consent: boolean;
};

const empty: FormState = {
  email: "", first_name: "", last_name: "", company: "", job_title: "",
  industry: "", phone: "", chapter: "", heard_about: "", consent: false,
};

const VisitMeeting = () => {
  const [form, setForm] = useState<FormState>(empty);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fieldErrs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        fieldErrs[issue.path[0] as string] = issue.message;
      }
      setErrors(fieldErrs);
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const id = crypto.randomUUID();
      const { error } = await supabase
        .from("visit_requests")
        .insert({
          id,
          email: parsed.data.email,
          first_name: parsed.data.first_name,
          last_name: parsed.data.last_name,
          company: parsed.data.company,
          job_title: parsed.data.job_title,
          industry: parsed.data.industry,
          phone: parsed.data.phone || null,
          chapter: parsed.data.chapter,
          heard_about: parsed.data.heard_about,
        });

      if (error) throw error;

      // Fire-and-forget email notifications (don't block the user on failures)
      const chapterLabel = parsed.data.chapter === "washington" ? "Washington" : "Union";
      const templateData = {
        firstName: parsed.data.first_name,
        lastName: parsed.data.last_name,
        email: parsed.data.email,
        phone: parsed.data.phone || "",
        company: parsed.data.company,
        jobTitle: parsed.data.job_title,
        industry: parsed.data.industry,
        chapter: chapterLabel,
        heardAbout: parsed.data.heard_about,
      };
      void Promise.allSettled([
        supabase.functions.invoke("send-public-transactional-email", {
          body: {
            templateName: "visit-request-committee",
            idempotencyKey: `visit-committee-${id}`,
            templateData,
          },
        }),
        supabase.functions.invoke("send-public-transactional-email", {
          body: {
            templateName: "visit-request-confirmation",
            recipientEmail: parsed.data.email,
            idempotencyKey: `visit-confirm-${id}`,
            templateData: {
              firstName: parsed.data.first_name,
              chapter: chapterLabel,
              email: parsed.data.email,
            },
          },
        }),
      ]);

      setSubmitted(true);
      setForm(empty);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Visit a Meeting</h1>
              <p className="text-lg text-muted-foreground">
                Curious about RCN? Tell us a bit about yourself and our membership
                committee will follow up with meeting details.
              </p>
            </div>

            {submitted ? (
              <Card>
                <CardContent className="pt-10 pb-10 text-center">
                  <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-4" />
                  <h2 className="text-2xl font-bold mb-2">Request received!</h2>
                  <p className="text-muted-foreground mb-6">
                    Thanks for reaching out. A confirmation is on its way to your
                    inbox and a member of our committee will be in touch soon.
                  </p>
                  <Button onClick={() => setSubmitted(false)} variant="outline">
                    Submit another request
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Your details</CardTitle>
                  <CardDescription>All fields are required except phone.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="First name" error={errors.first_name}>
                        <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} />
                      </Field>
                      <Field label="Last name" error={errors.last_name}>
                        <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} />
                      </Field>
                    </div>

                    <Field label="Email" error={errors.email}>
                      <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
                    </Field>

                    <Field label="Phone (optional)" error={errors.phone}>
                      <Input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Company" error={errors.company}>
                        <Input value={form.company} onChange={(e) => update("company", e.target.value)} />
                      </Field>
                      <Field label="Job title" error={errors.job_title}>
                        <Input value={form.job_title} onChange={(e) => update("job_title", e.target.value)} />
                      </Field>
                    </div>

                    <Field label="Industry" error={errors.industry}>
                      <Input value={form.industry} onChange={(e) => update("industry", e.target.value)} />
                    </Field>

                    <Field label="Chapter you'd like to visit" error={errors.chapter}>
                      <Select value={form.chapter} onValueChange={(v) => update("chapter", v as any)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a chapter" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="washington">Washington, MO</SelectItem>
                          <SelectItem value="union">Union, MO</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="How did you hear about RCN?" error={errors.heard_about}>
                      <Textarea
                        rows={3}
                        value={form.heard_about}
                        onChange={(e) => update("heard_about", e.target.value)}
                      />
                    </Field>

                    <div className="flex items-start gap-3 pt-2">
                      <Checkbox
                        id="consent"
                        checked={form.consent}
                        onCheckedChange={(v) => update("consent", !!v)}
                      />
                      <div className="space-y-1">
                        <Label htmlFor="consent" className="cursor-pointer leading-snug">
                          By submitting this form, I agree to be contacted by email
                          regarding my visit request.
                        </Label>
                        {errors.consent && (
                          <p className="text-sm text-destructive">{errors.consent}</p>
                        )}
                      </div>
                    </div>

                    <Button type="submit" disabled={submitting} className="w-full" size="lg">
                      {submitting ? "Submitting..." : "Submit Request"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const Field = ({
  label, error, children,
}: { label: string; error?: string; children: React.ReactNode }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    {children}
    {error && <p className="text-sm text-destructive">{error}</p>}
  </div>
);

export default VisitMeeting;
